// Stage1Card — "the answer, before the map". A card over the dimmed canvas
// that states the verdict (portfolio + this scenario) before any spatial
// reading is asked of the user. Stage 2 reveals the story & evidence.
//
// Every number is real: the portfolio line renders only from completed suite
// rows; the scenario verdict comes from the actual run in the store.

import { useDispatch, useSelector } from 'react-redux';
import {
  selectManifest,
  selectActiveScenarioId,
  selectActiveSource,
  selectWebSocketState,
} from '../../store/slices/scenarioSourceSlice.ts';
import {
  selectSimulationStatus,
  selectEffectiveAssertionSummary,
  selectScenarioVerdict,
  selectSuiteResults,
} from '../../store/slices/simulationSlice.ts';
import { setScenarioStage } from '../../store/slices/uiSlice.ts';
import {
  classifyScenarioType,
  deriveEffectiveVerdict,
  KIND_GLYPHS,
  KIND_NAMES,
  TYPE_CHIP_STYLES,
  VERDICT_CHIP_STYLES,
} from '../simulation/scenarioBadges.ts';
import {
  verdictMapFromSuite,
  tallyVerdicts,
  ledgerVerdict,
  sourceDisplayLabel,
  LEDGER_KIND_ORDER,
} from './ledgerData.ts';

/** Per-kind teaching copy shown on the card (design v2 voice). */
const TEACH: Record<string, string> = {
  happy:
    'You’re looking at the intended journey, end to end. Stage ② plays the walk step by step — then every lit node carries its proof: click one for the evidence.',
  failure:
    'You’re looking at a failure-path — and if it passes, that’s the point: the design fails exactly where it declared it would. Stage ② plays the walk and opens the evidence under every node it lights.',
  negative:
    'This scenario hands the design a broken precondition and passes only if the design refuses. A ⛔ STOP on the canvas is the product working.',
  timeout:
    'A saga clock elapses here. Passing means the design stops exactly where the timeout declares it must — nothing more runs.',
  alt: 'An alternate declared route through the flow. Stage ② plays the walk and opens the evidence under every node it lights.',
};

export default function Stage1Card() {
  const dispatch = useDispatch();
  const manifest = useSelector(selectManifest);
  const activeScenarioId = useSelector(selectActiveScenarioId);
  const activeSource = useSelector(selectActiveSource);
  const wsState = useSelector(selectWebSocketState);
  const status = useSelector(selectSimulationStatus);
  const summary = useSelector(selectEffectiveAssertionSummary);
  const storeVerdict = useSelector(selectScenarioVerdict);
  const results = useSelector(selectSuiteResults);

  let entry = null;
  if (manifest && activeScenarioId) {
    for (const flow of manifest.flows) {
      const e = flow.scenarios.find((s) => s.scenarioId === activeScenarioId);
      if (e) {
        entry = e;
        break;
      }
    }
  }

  const kind = entry ? classifyScenarioType(entry) : 'alt';
  const kindChip = TYPE_CHIP_STYLES[kind];
  const verdict = deriveEffectiveVerdict(status, summary, storeVerdict);
  const verdictChip = verdict !== null ? VERDICT_CHIP_STYLES[verdict] : null;

  // Portfolio line — only from completed suite rows.
  const allEntries = manifest?.flows.flatMap((f) => f.scenarios) ?? [];
  const verdicts = verdictMapFromSuite(results);
  const tally = tallyVerdicts(allEntries, verdicts);
  const overall = ledgerVerdict(tally);
  const suiteComplete = tally.total > 0 && tally.notRun === 0;

  const kindCounts: Partial<Record<string, number>> = {};
  for (const e of allEntries) {
    const k = classifyScenarioType(e);
    kindCounts[k] = (kindCounts[k] ?? 0) + 1;
  }
  const kindLine = LEDGER_KIND_ORDER.filter((k) => kindCounts[k])
    .map((k) => `${KIND_GLYPHS[k]}${kindCounts[k]}`)
    .join(' ');

  return (
    <div className="fv2-stage1-scrim">
      <div className="fv2-stage1-card">
        <div className="fv2-stage1-head">
          <div className="fv2-kicker">THE ANSWER, BEFORE THE MAP</div>
          {suiteComplete ? (
            overall === 'pass' ? (
              <div className="fv2-stage1-headline">
                This design holds —{' '}
                <b style={{ color: 'var(--green)' }}>
                  {tally.pass} of {tally.total}
                </b>{' '}
                scenario proofs pass.
              </div>
            ) : (
              <div className="fv2-stage1-headline">
                <b style={{ color: overall === 'fail' ? 'var(--red)' : '#fb923c' }}>
                  {tally.pass} of {tally.total}
                </b>{' '}
                scenario proofs pass{tally.fail > 0 ? ` — ${tally.fail} fail` : ''}
                {tally.error > 0 ? ` — ${tally.error} error` : ''}.
              </div>
            )
          ) : (
            <div className="fv2-stage1-headline">
              {verdict === 'pass' && (
                <>
                  This proof <b style={{ color: 'var(--green)' }}>passes</b>.
                </>
              )}
              {verdict === 'fail' && (
                <>
                  This proof <b style={{ color: 'var(--red)' }}>fails</b>.
                </>
              )}
              {verdict === 'error' && (
                <>
                  This run <b style={{ color: '#fb923c' }}>aborted</b> — an error, not a pass.
                </>
              )}
              {verdict === 'none' && <>This run produced no assertions — inconclusive.</>}
              {verdict === null && <>Replaying this scenario…</>}
            </div>
          )}
          <div className="fv2-stage1-meta">
            {sourceDisplayLabel(activeSource, wsState.specFile)}
            {manifest && <> · {manifest.flows.length} FLOWS</>}
            {kindLine && <> · KINDS {kindLine}</>}
            {suiteComplete && (
              <>
                {' '}
                · {tally.fail} FAILED · {tally.skipped + tally.notRun} SKIPPED
              </>
            )}
          </div>
        </div>

        <div className="fv2-stage1-body">
          <div className="fv2-stage1-chips">
            <span className="fv2-chip">
              <span style={{ color: kindChip.color }}>{KIND_GLYPHS[kind]}</span> KIND ·{' '}
              {KIND_NAMES[kind]}-PATH
            </span>
            {verdictChip ? (
              <span
                className="fv2-chip"
                style={{
                  color: verdictChip.color,
                  borderColor: 'currentcolor',
                  background: verdictChip.bg,
                }}
              >
                {verdict === 'pass'
                  ? '✓'
                  : verdict === 'fail'
                    ? '✗'
                    : verdict === 'error'
                      ? '⚠'
                      : '—'}{' '}
                VERDICT · {verdictChip.label}
              </span>
            ) : (
              <span className="fv2-chip fv2-dim">VERDICT · RUNNING…</span>
            )}
            <span className="fv2-chip fv2-dim">BEHIND THIS CARD: ITS CANVAS</span>
          </div>
          <div className="fv2-stage1-label">{entry?.scenarioLabel ?? activeScenarioId}</div>
          {entry?.description && <p className="fv2-stage1-desc">{entry.description}</p>}
          <p className="fv2-stage1-teach">{TEACH[kind]}</p>
        </div>

        <div className="fv2-stage1-actions">
          <button
            className="fv2-card-btn fv2-btn-green"
            onClick={() => dispatch(setScenarioStage(2))}
          >
            ENTER THE STORY →
          </button>
          <span className="fv2-stage1-keys">
            OR PRESS <b>2</b> · CLICK ANY LIT NODE FOR ITS EVIDENCE
          </span>
        </div>
      </div>
    </div>
  );
}
